import React, { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import moment from 'moment';
import { toast } from 'react-toastify';
import PropTypes from 'prop-types';
import accountHelpers from '../../../../../utils/accountHelpers';
import {
  addScriptNoteRequest, deleteScriptNoteRequest, editScriptNoteRequest,
  getScriptsNotesRequest, scriptNoteOrAttachmentIds,
} from '../../../../../store/actions/script';
import classes from './tabs.module.scss';
import avatar from '../../../../../assets/icons/avatar.png';
import Button from '../../../../_common/Form/Buttons/Button';
import NoteModal from '../../../../_common/Modal/NoteModal/NoteModal';
import AttentionModal from '../../../../_common/Modal/AttentionModal/AttentionModal';
import { ReactComponent as EditIcon } from '../../../../../assets/icons/edit.svg';
import { ReactComponent as DeleteIcon } from '../../../../../assets/icons/delete.svg';
import Loader from '../../../../_common/Loader/Loader';
import Checkbox from '../../../../_common/Form/Checkbox/Checkbox';
import Utils from '../../../../../utils/utils';

const Notes = ({ changeCount }) => {
  const isPatient = accountHelpers.isInOneOfRoles(['Patient', 'Health', 'Manufacturer Users']);
  const isManufacturerUsers = accountHelpers.isInOneOfRoles(['Manufacturer Users']);
  const isAdmin = accountHelpers.isInOneOfRoles(['Admin']);

  const historyTabRef = document.getElementById('rx-history-id');

  const dispatch = useDispatch();
  const {
    scriptNotes, notesRequestStatus, notesId, scrollingToNoteId,
  } = useSelector((store) => store.script);
  const { scriptId } = useParams();
  const noteRef = useRef(null);

  const defaultData = {
    scriptId,
    name: accountHelpers.getUser().name,
    note: '',
    privateBody: false,
    userId: accountHelpers.getUser().id,
  };

  const [isOpenModal, setIsOpenModal] = useState(false);
  const [requestData, setRequestData] = useState(defaultData);
  const [noteDate, setNoteDate] = useState({
    date: moment().format('YYYY/MM/DD'),
    time: moment().format('hh:mm A'),
  });

  const [dateError, setDateError] = useState({});

  const [deletingNoteId, setDeletingNoteId] = useState('');

  const [saveLoading, setSaveLoading] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const allScriptNoteIds = scriptNotes.map((s) => s.noteId);
  const isCheckedAllScriptNoteIds = allScriptNoteIds.every((id) => notesId.includes(id));

  useEffect(() => {
    dispatch(getScriptsNotesRequest({
      scriptId,
      userId: accountHelpers.getUser().id,
    }));

    dispatch(scriptNoteOrAttachmentIds({ name: 'note', ids: [] }));
  }, [scriptId]);

  useEffect(() => {
    if (scrollingToNoteId && notesRequestStatus === 'success') {
      Utils.animateScrollTo(`#notes_id_${scrollingToNoteId}`, -80);
    }
  }, [scrollingToNoteId, notesRequestStatus]);

  const changeNoteData = (path, value) => {
    setRequestData((prev) => ({ ...prev, [path]: value }));
  };

  const changeNoteDate = (path, value) => {
    setNoteDate((prev) => ({ ...prev, [path]: value }));

    setDateError((prev) => ({ ...prev, [path]: false }));
  };

  const getNotes = () => dispatch(getScriptsNotesRequest({
    scriptId,
    userId: accountHelpers.getUser().id,
  }));

  const closeModal = () => {
    setRequestData(defaultData);
    setNoteDate({ date: moment().format('YYYY/MM/DD'), time: moment().format('hh:mm A') });
    setDateError({});
    setIsOpenModal(false);
  };

  const openEditModal = (note) => {
    const sendDate = moment.utc(note.scriptNote.sendDate || note.createdAt).local();

    setRequestData({
      scriptId,
      note: note.scriptNote.note,
      privateBody: !!note.scriptNote.private,
      noteId: note.noteId,
    });

    setNoteDate({
      date: sendDate.format('YYYY/MM/DD'),
      time: sendDate.format('hh:mm A'),
    });
    setDateError({});
    setIsOpenModal(true);
  };

  const sendNote = async () => {
    const { date, time } = noteDate;

    if (!requestData.note) {
      toast.error('Please write note');
      return;
    }

    if (date.length !== 10 || time.length !== 8) {
      setDateError({ date: date.length !== 10, time: time.length !== 8 });

      toast.error('Please fill date and time');
      return;
    }

    const sendingData = { ...requestData };

    if (isAdmin) {
      sendingData.sendDate = moment.utc(new Date(`${date} ${time}`)).format('YYYY-MM-DD HH:mm');
    }

    setSaveLoading(true);

    const { status } = requestData.noteId
      ? await dispatch(editScriptNoteRequest(sendingData))

      : await dispatch(addScriptNoteRequest(sendingData));

    setSaveLoading(false);

    if (status === 'ok') {
      await getNotes();

      if (!requestData.noteId) changeCount('Notes');

      closeModal();

      if (noteRef.current) window.scrollTo(0, noteRef.current.offsetTop);
    }
  };

  const onDeleteNoteClick = async () => {
    setDeleteLoading(true);

    const { status } = await dispatch(deleteScriptNoteRequest({ noteId: deletingNoteId }));

    if (status === 'ok') {
      changeCount('Notes', -1);

      setDeletingNoteId('');
    }

    setDeleteLoading(false);
  };

  const onCheckBoxChange = (ids) => {
    if (ids === 'all') {
      if (isCheckedAllScriptNoteIds) {
        dispatch(scriptNoteOrAttachmentIds({ name: 'note', ids: [] }));
      } else {
        dispatch(scriptNoteOrAttachmentIds({ name: 'note', ids: allScriptNoteIds }));
      }
    } else if (notesId.some((s) => s === ids)) {
      dispatch(scriptNoteOrAttachmentIds({ name: 'note', ids: notesId.filter((s) => s !== ids) }));
    } else {
      dispatch(scriptNoteOrAttachmentIds({ name: 'note', ids: [...notesId, ids] }));
    }
  };

  return (
    notesRequestStatus !== 'success'
      ? (
        <div className={classes.note_loader}>
          <Loader />
        </div>
      )
      : (
        <div ref={noteRef} className={classes.script_note_wrapper}>
          <div className={classes.note_copy}>
            {notesRequestStatus === 'success' && !!scriptNotes.length && !isPatient
                && (
                <div>
                  <Checkbox
                    labelMarginLeft={14}
                    onChange={() => onCheckBoxChange('all')}
                    label="Copy to all"
                    checked={isCheckedAllScriptNoteIds}
                  />
                </div>
                )}

            {!!notesId.length && (
            <div>
              <div className={classes.note_button_header}>
                <Button
                  reverseColor
                  title="Copy"
                  onClick={() => historyTabRef.click()}
                  buttonColor="#5F6C8E"
                />
              </div>

              {/* <div className={classes.note_button_header}> */}
              {/*  <Button */}
              {/*    buttonColor="#5F6C8E" */}
              {/*    title="Cancel" */}
              {/*    onClick={() => dispatch(notesId([]))} */}
              {/*  /> */}
              {/* </div> */}
            </div>
            )}

          </div>

          <div>
            {!scriptNotes.length
              ? <div className={classes.notResult}>No Data Found</div>

              : (scriptNotes.map((s) => (
                <div
                  key={s.noteId}
                  id={`notes_id_${s.noteId}`}
                  className={`${classes.note_wrapper} ${notesId.includes(s.noteId)
                    ? classes.active : ''}`}
                >
                  {!isPatient && (
                  <div className={classes.note_checkbox}>
                    <Checkbox
                      onChange={() => onCheckBoxChange(s.noteId)}
                      checked={notesId.includes(s.noteId)}
                      disabled={!!isPatient}
                    />
                  </div>
                  )}

                  <div className={classes.note_img}>
                    <img
                      src={s.User.link}
                      alt="avatar"
                      onError={(e) => {
                        e.target.onerror = null;
                        e.target.src = avatar;
                      }}
                    />
                  </div>

                  <div className={classes.note_text_wrapper}>
                    <div>
                      <div>
                        <span>{s.User.name}</span>

                        <span>
                          {moment.utc(s.scriptNote.sendDate || s.createdAt).local().calendar({
                            sameDay: 'LT',
                            nextDay: 'MM.DD LT',
                            nextWeek: 'MM.DD LT',
                            lastDay: 'MM.DD LT',
                            lastWeek: 'MM.DD LT',
                            sameElse: 'MM.DD.YYYY LT',
                          })}
                        </span>
                      </div>

                      {/* {!isPatient && <span>{`Make ${(s.scriptNote.private ? 'public' : 'private')}`}</span>} */}

                      {isAdmin && (
                        <div className={classes.note_actions}>
                          <EditIcon className={classes.edit_icon} onClick={() => openEditModal(s)} />

                          <DeleteIcon onClick={() => setDeletingNoteId(s.noteId)} />
                        </div>
                      )}
                    </div>

                    <p>{s.scriptNote.note}</p>
                  </div>
                </div>
              )))}
          </div>

          {!isManufacturerUsers && (
            <div className={classes.note_button_wrapper}>
              <Button
                reverseColor
                title="Post"
                onClick={() => setIsOpenModal(true)}
                addPostButton
              />
            </div>
          )}

          <NoteModal
            isOpenModal={isOpenModal}
            onClose={closeModal}
            noteData={requestData}
            onChangeNoteData={changeNoteData}
            saveLoading={saveLoading}
            showDateTime={isAdmin}
            noteDate={noteDate}
            onChangeDate={changeNoteDate}
            dateError={dateError}
            saveNote={sendNote}
          />

          <AttentionModal
            isOpen={!!deletingNoteId}
            onClose={() => setDeletingNoteId('')}
            onSave={onDeleteNoteClick}
            loading={deleteLoading}
            title="Delete Note?"
            text="Are you sure you want to delete this note?"
          />
        </div>
      )
  );
};

Notes.propTypes = {
  changeCount: PropTypes.func.isRequired,
};

export default Notes;
